import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { formatElapsedTime, getAccessibilityLabel, clockTokens, describeLocationFailure, sanitizeClockErrorMessage, describeOpenCheckIn, OPEN_CHECK_IN_HINT, } from './utils';
import { useWorkSession } from '../../hooks/useWorkSession';
import { useDeviceInfo } from '../../hooks/useDeviceInfo';
import { useGeofenceCheck } from '../../hooks/useGeofenceCheck';
import { useSessionPolicy } from '../../hooks/useSessionPolicy';
import { useBreakTypes } from '../../hooks/useBreakTypes';
import { buildClockInPayload } from './clockInPayload';
import { ClockOutPromptModal } from './ClockOutPromptModal.web';
import { apiClient } from '../../services/api';
/**
 * Helper hook to refresh work session when tab becomes visible or window gains focus
 * This handles the case where a session is started on another device
 */
function useVisibilityRefresh(refresh) {
    // Initialize to current time so the first focus/visibility event after mount is debounced
    // (prevents duplicate refresh when the init effect already fires on mount)
    const lastRefreshRef = React.useRef(Date.now());
    useEffect(() => {
        const handleRefresh = () => {
            // Debounce: don't refresh more than once per 5 seconds
            const now = Date.now();
            if (now - lastRefreshRef.current < 5000) {
                return;
            }
            lastRefreshRef.current = now;
            refresh();
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                handleRefresh();
            }
        };
        const handleFocus = () => {
            handleRefresh();
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [refresh]);
}
/**
 * WorkSessionClock - Web Implementation
 *
 * A compact time clock component for the header that allows users to
 * clock in/out and see their current work session duration.
 *
 * Features:
 * - Click to toggle clock in/out
 * - Live elapsed time display when clocked in
 * - Loading spinner during API calls
 * - Error indication with tooltip
 * - Keyboard accessible
 * - ARIA labels for screen readers
 */
export const WorkSessionClock = ({ size = 'compact', showLabel, clockedInLabel = 'Working', clockedOutLabel = 'Check In', onStatusChange, onError, disabled = false, testID = 'work-session-clock', accountId, locationId, bypassGeofence = false, }) => {
    // Get API base URL for SSE (web uses cookies for auth, no token needed)
    const sseBaseUrl = apiClient.getBaseUrl();
    // Session policy + geofence preflight config, shared logic with mobile.
    // Uses the shared apiClient — a raw fetch here bypassed CSRF headers and
    // the 401 logout handler. Re-fetched on tab focus/visibility below.
    const { geofenceConfig, refresh: refreshGeofencePolicy } = useSessionPolicy(apiClient, accountId, locationId, 'timeclock', {
        bypassGeofence,
    });
    const { checkPosition } = useGeofenceCheck(geofenceConfig);
    // Account-configured break types for the clock-out modal (falls back to
    // the modal's built-in defaults until loaded / when none are configured).
    const { breakTypes } = useBreakTypes(apiClient, accountId);
    const { isClockedIn, isLoading, isInitialized, error, elapsedMs, isOnBreak, clockIn, clockOut, startBreak, endBreak, clearError, refresh, isSSEConnected, openElsewhere, dismissOpenElsewhere, noLocationAssigned, dismissNoLocationAssigned, } = useWorkSession({
        enableSSE: true,
        sseBaseUrl,
    });
    // Refresh session AND geofence policy when tab becomes visible / regains
    // focus. Cross-device session sync plus picking up geofence edits an admin
    // made elsewhere without a full reload.
    const refreshOnVisible = useCallback(async () => {
        await Promise.all([refresh(), refreshGeofencePolicy()]);
    }, [refresh, refreshGeofencePolicy]);
    useVisibilityRefresh(refreshOnVisible);
    const { getClockInData } = useDeviceInfo();
    // Local error state for client-side geofence preflight errors
    const [geofenceError, setGeofenceError] = useState(null);
    // Single error surface: an absolutely-positioned popover under the button.
    // Opens when an error lands, auto-hides after a beat (the dot stays while
    // the error is set; hover/focus reopens), closes on its ✕. Rendered out of
    // document flow ON PURPOSE — the previous in-flow block grew the fixed-
    // height header row and shifted the whole top bar whenever an error showed.
    const [errorPopoverOpen, setErrorPopoverOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    // Local processing state for immediate visual feedback (before API loading state kicks in)
    const [isProcessing, setIsProcessing] = useState(false);
    // Track if component is mounted to prevent state updates after unmount/navigation
    const isMountedRef = useRef(true);
    // Track ongoing operation to prevent double-clicks
    const operationInProgressRef = useRef(false);
    // Show clock out prompt modal
    const [showClockOutModal, setShowClockOutModal] = useState(false);
    // Cleanup on unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    // Combined loading state - show spinner if either local processing or API loading
    const showLoadingState = isProcessing || isLoading;
    // Merge geofence error with hook error for display. Sanitized: the hook's
    // error channel can carry raw server payloads (JSON bodies, HTTP-status
    // strings) that read as garbage in a compact popover.
    const displayError = useMemo(() => {
        const raw = geofenceError || error;
        return raw ? sanitizeClockErrorMessage(raw) : null;
    }, [geofenceError, error]);
    // Surface each new error immediately, then auto-hide the popover; the
    // error dot remains as the persistent indicator and reopens it on hover.
    useEffect(() => {
        if (!displayError) {
            setErrorPopoverOpen(false);
            return;
        }
        setErrorPopoverOpen(true);
        const timer = setTimeout(() => setErrorPopoverOpen(false), 10000);
        return () => clearTimeout(timer);
    }, [displayError]);
    // Determine if label should be shown
    const shouldShowLabel = showLabel ?? size === 'normal';
    // Format the elapsed time
    const formattedTime = useMemo(() => formatElapsedTime(elapsedMs), [elapsedMs]);
    // Get size-specific tokens
    const sizeTokens = clockTokens.sizes[size];
    // Determine current color state
    const colorState = useMemo(() => {
        if (disabled)
            return clockTokens.colors.disabled;
        if (showLoadingState || !isInitialized)
            return clockTokens.colors.loading;
        if (displayError)
            return clockTokens.colors.error;
        if (isOnBreak)
            return clockTokens.colors.onBreak;
        return isClockedIn ? clockTokens.colors.active : clockTokens.colors.inactive;
    }, [disabled, showLoadingState, isInitialized, displayError, isOnBreak, isClockedIn]);
    // Handle click
    const handleClick = useCallback(async () => {
        // Prevent action if disabled, already loading, or operation in progress
        if (disabled || showLoadingState || operationInProgressRef.current)
            return;
        // Clear any previous error
        if (error) {
            clearError();
        }
        if (geofenceError) {
            setGeofenceError(null);
        }
        // If clocked in and NOT on break, show the clock out prompt modal
        if (isClockedIn && !isOnBreak) {
            setShowClockOutModal(true);
            return;
        }
        // If on break, end the break and resume work
        if (isOnBreak) {
            operationInProgressRef.current = true;
            setIsProcessing(true);
            try {
                const success = await endBreak();
                if (isMountedRef.current && success) {
                    onStatusChange?.(true); // Still clocked in after ending break
                }
            }
            catch (err) {
                if (isMountedRef.current) {
                    const errorMessage = err instanceof Error ? err.message : 'An error occurred';
                    onError?.(errorMessage);
                }
            }
            finally {
                if (isMountedRef.current) {
                    setIsProcessing(false);
                }
                operationInProgressRef.current = false;
            }
            return;
        }
        // Clock in - collect device info immediately (sync), location with timeout
        operationInProgressRef.current = true;
        setIsProcessing(true);
        try {
            const { device, location, locationFailure } = await getClockInData();
            // Check if component is still mounted before proceeding
            if (!isMountedRef.current)
                return;
            // Client-side preflight, for the ONE thing the client can still judge
            // for itself: whether it has a GPS fix at all. It must NOT judge
            // whether the fix is inside a fence — it only knows the fence of the
            // location the app is switched to, while the server checks the fences
            // of EVERY location this person is assigned to and passes on the first
            // match. Blocking here would refuse someone standing at another of
            // their own shops, which is exactly the check-in the account wants to
            // allow. Send it and let the server place the punch.
            const geoResult = checkPosition(location?.latitude, location?.longitude);
            if (!geoResult.allowed && geoResult.errorCode === 'GEOFENCE_GPS_REQUIRED') {
                // Say WHY the fix is missing — a blocked browser permission, an
                // unsupported browser and a flaky fix each need a different action.
                const message = describeLocationFailure(locationFailure);
                setGeofenceError(message);
                setIsProcessing(false);
                operationInProgressRef.current = false;
                onError?.(message);
                // Auto-dismiss after 8 seconds
                setTimeout(() => {
                    if (isMountedRef.current)
                        setGeofenceError(null);
                }, 8000);
                return;
            }
            const success = await clockIn(buildClockInPayload(device, location, locationId));
            // Only update state if component is still mounted
            if (isMountedRef.current && success) {
                onStatusChange?.(true);
            }
        }
        catch (err) {
            // Only report error if component is still mounted
            if (isMountedRef.current) {
                const errorMessage = err instanceof Error ? err.message : 'An error occurred';
                onError?.(errorMessage);
            }
        }
        finally {
            // Reset processing state only if component is still mounted
            if (isMountedRef.current) {
                setIsProcessing(false);
            }
            operationInProgressRef.current = false;
        }
    }, [
        disabled,
        showLoadingState,
        error,
        geofenceError,
        isClockedIn,
        isOnBreak,
        clockIn,
        endBreak,
        clearError,
        checkPosition,
        onStatusChange,
        onError,
        getClockInData,
        locationId,
    ]);
    // Handle end shift from modal
    const handleEndShift = useCallback(async () => {
        const locationPromise = getClockInData().catch(() => ({ device: null, location: null }));
        const locationData = await Promise.race([
            locationPromise,
            new Promise((resolve) => setTimeout(() => resolve({ device: null, location: null }), 1500)),
        ]);
        const success = await clockOut({
            latitude: locationData.location?.latitude,
            longitude: locationData.location?.longitude,
            gps_accuracy_meters: locationData.location?.gps_accuracy_meters,
        });
        if (success) {
            onStatusChange?.(false);
        }
    }, [clockOut, getClockInData, onStatusChange]);
    // Handle start break from modal
    const handleStartBreak = useCallback(async (breakTypeId, breakTypeName) => {
        const success = await startBreak({
            break_type_id: breakTypeId,
            break_type_name: breakTypeName,
        });
        if (success) {
            // Still clocked in but now on break
            onStatusChange?.(true);
        }
    }, [startBreak, onStatusChange]);
    // Handle keyboard interaction
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleClick();
        }
    }, [handleClick]);
    // Accessibility label
    const ariaLabel = `${getAccessibilityLabel(isClockedIn, isLoading, formattedTime)}${''}`;
    // Container styles
    const containerStyle = {
        position: 'relative',
        display: 'inline-flex',
    };
    // Button styles
    const buttonStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: sizeTokens.gap,
        height: sizeTokens.height,
        padding: `0 ${sizeTokens.padding}px`,
        borderRadius: sizeTokens.borderRadius,
        border: `1px solid ${colorState.border}`,
        backgroundColor: isHovered && !disabled && !showLoadingState
            ? isClockedIn
                ? clockTokens.colors.active.backgroundHover
                : clockTokens.colors.inactive.backgroundHover
            : colorState.background,
        color: colorState.text,
        cursor: disabled || showLoadingState ? 'not-allowed' : 'pointer',
        transition: `all ${clockTokens.animation.duration} ${clockTokens.animation.easing}`,
        outline: 'none',
        fontFamily: 'inherit',
        fontSize: sizeTokens.fontSize,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        userSelect: 'none',
    };
    // Clock icon SVG
    const ClockIcon = () => (_jsxs("svg", { width: sizeTokens.iconSize, height: sizeTokens.iconSize, viewBox: "0 0 24 24", fill: "none", stroke: colorState.icon, strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", style: { flexShrink: 0 }, children: [_jsx("circle", { cx: "12", cy: "12", r: "10" }), _jsx("polyline", { points: "12 6 12 12 16 14" })] }));
    // Play icon (for clock in)
    const PlayIcon = () => (_jsx("svg", { width: sizeTokens.iconSize, height: sizeTokens.iconSize, viewBox: "0 0 24 24", fill: colorState.icon, style: { flexShrink: 0 }, children: _jsx("polygon", { points: "5 3 19 12 5 21 5 3" }) }));
    // Stop icon (for clock out)
    const StopIcon = () => (_jsx("svg", { width: sizeTokens.iconSize, height: sizeTokens.iconSize, viewBox: "0 0 24 24", fill: colorState.icon, style: { flexShrink: 0 }, children: _jsx("rect", { x: "6", y: "6", width: "12", height: "12", rx: "1" }) }));
    // Loading spinner
    const LoadingSpinner = () => (_jsxs("svg", { width: sizeTokens.iconSize, height: sizeTokens.iconSize, viewBox: "0 0 24 24", fill: "none", stroke: colorState.icon, strokeWidth: "2", strokeLinecap: "round", style: {
            flexShrink: 0,
            animation: 'spin 1s linear infinite',
        }, children: [_jsx("path", { d: "M21 12a9 9 0 11-18 0 9 9 0 0118 0z", opacity: "0.25" }), _jsx("path", { d: "M21 12a9 9 0 00-9-9" }), _jsx("style", { children: `
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      ` })] }));
    // Error indicator dot
    const ErrorDot = () => (_jsx("span", { style: {
            position: 'absolute',
            top: -2,
            right: -2,
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: clockTokens.colors.error.border,
            border: '1px solid #000',
        } }));
    // Error popover — the single error surface. Absolutely positioned below
    // the button and RIGHT-aligned (the clock lives near the right edge of the
    // header, so left/center alignment can push long messages off-screen).
    // Out of document flow so an error never changes the header's height or
    // shifts its neighbors. role="alert" announces the message to screen
    // readers when it appears.
    const ErrorPopover = () => {
        if (!errorPopoverOpen || !displayError)
            return null;
        return (_jsxs("div", { role: "alert", style: {
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: 8,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: '8px 10px',
                backgroundColor: '#1A1A1A',
                border: `1px solid ${clockTokens.colors.error.border}`,
                borderRadius: 6,
                color: '#FFFFFF',
                fontSize: 12,
                lineHeight: 1.45,
                zIndex: 10000,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                width: 'max-content',
                maxWidth: 'min(320px, 80vw)',
                overflowWrap: 'anywhere',
                whiteSpace: 'normal',
                textAlign: 'left',
                cursor: 'default',
            }, children: [_jsx("span", { style: { flex: 1, minWidth: 0 }, children: displayError }), _jsx("button", { "aria-label": "Dismiss error", onClick: (e) => {
                        e.stopPropagation();
                        setErrorPopoverOpen(false);
                    }, style: {
                        flexShrink: 0,
                        width: 18,
                        height: 18,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'transparent',
                        border: 'none',
                        borderRadius: 4,
                        color: '#999999',
                        cursor: 'pointer',
                        fontSize: 13,
                        lineHeight: 1,
                        padding: 0,
                    }, children: "\u00D7" })] }));
    };
    /**
     * Shared ground for the two things the clock has to ASK about, drawn like
     * the error popover so the header never changes height.
     */
    const PromptPopover = ({ children, testId }) => (_jsx("div", { 
        // Not alertdialog: that promises a modal — accessible name, focus moved
        // in, escape to leave — and this is a non-modal popover under the clock
        // with none of that. role="alert" announces it and promises only what
        // it delivers; the dismiss button is reachable in DOM order.
        role: "alert", "data-testid": testId, style: {
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 8,
            padding: '10px 12px',
            backgroundColor: '#1A1A1A',
            border: '1px solid rgba(255,255,255,0.18)',
            borderRadius: 6,
            color: '#FFFFFF',
            fontSize: 12,
            lineHeight: 1.45,
            zIndex: 10000,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
            width: 'max-content',
            maxWidth: 'min(320px, 80vw)',
            overflowWrap: 'anywhere',
            textAlign: 'left',
            cursor: 'default',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
        }, onClick: (e) => e.stopPropagation(), children: children }));
    const promptButton = {
        background: 'rgba(255,255,255,0.1)',
        border: '1px solid rgba(255,255,255,0.18)',
        borderRadius: 5,
        color: '#FFFFFF',
        cursor: 'pointer',
        fontSize: 12,
        padding: '5px 10px',
        textAlign: 'left',
    };
    /**
     * A check-in is already running. ONE check-in covers the whole day and
     * follows the person between the account's locations, so there is no
     * second punch to make and nothing to move — but this tap started nothing,
     * and that used to be reported as success.
     *
     * Told as TIME, not place: their hours belong to them and the period, not
     * to the shop they punched in at, and naming that shop invites the "should
     * I move?" question this change has just answered.
     */
    const OpenElsewherePrompt = () => {
        if (!openElsewhere)
            return null;
        return (_jsxs(PromptPopover, { testId: "clock-open-elsewhere", children: [_jsx("span", { "data-testid": "clock-open-elsewhere-text", children: describeOpenCheckIn(openElsewhere.session.start_time) }), _jsx("span", { style: { color: '#999999' }, children: OPEN_CHECK_IN_HINT }), _jsx("button", { onClick: (e) => {
                        e.stopPropagation();
                        dismissOpenElsewhere?.();
                    }, style: promptButton, "data-testid": "clock-open-elsewhere-dismiss", children: "Got it" })] }));
    };
    /**
     * The person belongs to no location at all, in an account that fences
     * check-ins. There is nothing here they can pick their way out of — the
     * server places the punch from GPS now, and it has no fences of theirs to
     * match against because they have none.
     */
    const NoLocationAssignedPrompt = () => {
        if (!noLocationAssigned)
            return null;
        return (_jsxs(PromptPopover, { testId: "clock-no-location", children: [_jsx("span", { children: "You\u2019re not assigned to any location yet, so we can\u2019t check you in." }), _jsx("span", { style: { color: '#999999' }, children: "Ask an administrator to add you to a location, then try again." }), _jsx("button", { onClick: (e) => {
                        e.stopPropagation();
                        dismissNoLocationAssigned?.();
                    }, style: promptButton, "data-testid": "clock-no-location-dismiss", children: "Got it" })] }));
    };
    // Pause icon (for on break state)
    const PauseIcon = () => (_jsxs("svg", { width: sizeTokens.iconSize, height: sizeTokens.iconSize, viewBox: "0 0 24 24", fill: colorState.icon, style: { flexShrink: 0 }, children: [_jsx("rect", { x: "6", y: "4", width: "4", height: "16", rx: "1" }), _jsx("rect", { x: "14", y: "4", width: "4", height: "16", rx: "1" })] }));
    // Resume icon (play icon for returning from break)
    const ResumeIcon = () => (_jsx("svg", { width: sizeTokens.iconSize, height: sizeTokens.iconSize, viewBox: "0 0 24 24", fill: "#F59E0B", style: { flexShrink: 0 }, children: _jsx("polygon", { points: "5 3 19 12 5 21 5 3" }) }));
    // Render content based on state
    const renderContent = () => {
        if (showLoadingState || !isInitialized) {
            return (_jsxs(_Fragment, { children: [_jsx(LoadingSpinner, {}), shouldShowLabel && (_jsx("span", { children: isProcessing ? (isOnBreak ? 'Resuming...' : isClockedIn ? 'Checking out...' : 'Checking in...') : 'Loading...' }))] }));
        }
        // On break state - show resume option
        if (isOnBreak) {
            return (_jsxs(_Fragment, { children: [_jsx(ResumeIcon, {}), _jsx("span", { style: {
                            fontVariantNumeric: 'tabular-nums',
                            color: '#F59E0B',
                        }, children: "On Break" }), shouldShowLabel && _jsx("span", { style: { opacity: 0.8, fontSize: sizeTokens.fontSize - 1, color: '#F59E0B' }, children: "Tap to resume" })] }));
        }
        if (isClockedIn) {
            return (_jsxs(_Fragment, { children: [_jsx(StopIcon, {}), _jsx("span", { style: {
                            fontVariantNumeric: 'tabular-nums',
                            minWidth: size === 'compact' ? 'auto' : 50,
                        }, children: size === 'compact' ? formattedTime.displayShort : formattedTime.display }), shouldShowLabel && (_jsx("span", { style: { opacity: 0.8, fontSize: sizeTokens.fontSize - 1 }, "data-testid": "clock-running-label", children: clockedInLabel }))] }));
        }
        return (_jsxs(_Fragment, { children: [_jsx(PlayIcon, {}), shouldShowLabel && _jsx("span", { children: clockedOutLabel })] }));
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { style: { ...containerStyle, position: 'relative', display: 'flex', alignItems: 'center' }, children: [_jsx("button", { "data-testid": testID, style: buttonStyle, onClick: handleClick, onKeyDown: handleKeyDown, onMouseEnter: () => {
                            setIsHovered(true);
                            if (displayError)
                                setErrorPopoverOpen(true);
                        }, onMouseLeave: () => setIsHovered(false), onFocus: () => displayError && setErrorPopoverOpen(true), disabled: disabled, "aria-label": ariaLabel, "aria-pressed": isClockedIn, "aria-busy": isLoading, "aria-invalid": !!displayError, role: "button", tabIndex: disabled ? -1 : 0, children: renderContent() }), displayError && _jsx(ErrorDot, {}), _jsx(ErrorPopover, {}), _jsx(OpenElsewherePrompt, {}), _jsx(NoLocationAssignedPrompt, {})] }), _jsx(ClockOutPromptModal, { visible: showClockOutModal, onClose: () => setShowClockOutModal(false), onEndShift: handleEndShift, onStartBreak: handleStartBreak, isLoading: isLoading, elapsedTimeDisplay: formattedTime.display, breakTypes: breakTypes ?? undefined })] }));
};
export default WorkSessionClock;
